import type { ServiceAuthProps } from '@/interfaces/serviceAuth'
import { safeDecodeJwtPayload, summarizeClerkClaims } from '@/lib/jwt'

export async function logAuthDebug(prefix: string, sessionProps: ServiceAuthProps) {
  try {
    const token = await sessionProps.getToken()
    const payload = safeDecodeJwtPayload(token)
    const summary = summarizeClerkClaims(payload)

    const mismatch = summary && summary.org_id && summary.org_id !== sessionProps.orgId
    // Intentionally do not log the raw token
    console.debug(`[${prefix}] auth`, {
      userId: sessionProps.userId,
      orgId: sessionProps.orgId,
      orgSlug: sessionProps.orgSlug,
      hasToken: !!token,
      claims: summary,
      orgMismatch: !!mismatch,
    })
  } catch (e) {
    console.warn(`[${prefix}] failed to log auth debug`, e)
  }
}

/**
 * Validate that required org claims are present and not unresolved template strings.
 * Throws with a clear message in case of misconfiguration.
 */
export async function validateOrgClaimsOrThrow(prefix: string, sessionProps: ServiceAuthProps) {
  const token = await sessionProps.getToken()
  if (!token) {
    throw new Error(
      `${prefix}: Missing auth token. Ensure Clerk session is active and the 'supabase' JWT template is configured.`
    )
  }
  const payload = safeDecodeJwtPayload(token)
  const orgId =
    (payload?.['org_id'] as string) ||
    (payload?.['organization_id'] as string) ||
    ((payload?.['org_metadata'] as any)?.['id'] as string)
  const sub = payload?.['sub'] as string | undefined
  if (!orgId) {
    throw new Error(
      `${prefix}: JWT missing organization id (org_id/organization_id/org_metadata.id). Verify your Clerk 'supabase' JWT template includes these fields for this environment.`
    )
  }
  if (typeof orgId === 'string' && orgId.includes('{{')) {
    throw new Error(
      `${prefix}: JWT contains unresolved template for org id (${orgId}). Update the Clerk 'supabase' template to use the correct variables (e.g. {{org.id}} vs {{organization.id}}).`
    )
  }
  if (!sub) {
    // Clerk sets 'sub' automatically; warn if somehow absent
    console.warn(`${prefix}: JWT missing 'sub' claim. Clerk should populate this automatically.`)
  }
}
